import { fetchAuthSession } from 'aws-amplify/auth'
import { env } from '../config/env'
import type { ApiErrorBody } from '../types/api'

export class MaruyaApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody

  constructor(message: string, status: number, body: ApiErrorBody = {}) {
    super(message)
    this.name = 'MaruyaApiError'
    this.status = status
    this.body = body
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  auth?: boolean
  /** AbortSignal（タイムアウト等）。abort 時は MaruyaApiError(ask_timeout / aborted) に正規化しない — 呼び出し側で扱う */
  signal?: AbortSignal
}

async function getIdToken(forceRefresh = false): Promise<string> {
  const session = await fetchAuthSession({ forceRefresh })
  const token = session.tokens?.idToken?.toString()
  if (!token) {
    throw new MaruyaApiError('UNAUTHENTICATED', 401, { error: 'missing_token' })
  }
  return token
}

export async function maruyaApi<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  if (!env.isApiConfigured) {
    throw new Error('VITE_MARUYA_API_URL is not configured')
  }

  const { body, headers, auth = true, ...rest } = options

  const send = async (forceRefresh: boolean) => {
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(headers as Record<string, string> | undefined),
    }

    if (auth) {
      requestHeaders.Authorization = `Bearer ${await getIdToken(forceRefresh)}`
    }

    return fetch(`${env.maruyaApiUrl}${path}`, {
      ...rest,
      headers: requestHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  }

  let response = await send(false)

  if (auth && response.status === 401) {
    response = await send(true)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const raw = await response.text()
  let parsed: unknown = {}
  if (raw) {
    try {
      parsed = JSON.parse(raw)
    } catch {
      parsed = { error: raw || response.statusText }
    }
  }

  if (!response.ok) {
    const responseBody = parsed as ApiErrorBody
    if (responseBody.error === 'invalid_token_use') {
      console.error(
        '[maruya-api] Access Token was sent instead of ID Token',
        responseBody,
      )
    }
    throw new MaruyaApiError(
      responseBody.error ?? responseBody.message ?? response.statusText,
      response.status,
      responseBody,
    )
  }

  return parsed as T
}

export async function maruyaApiPublic<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return maruyaApi<T>(path, { ...options, auth: false })
}
